// Pure Ball Sort game logic — rules engine with no UI dependencies.
// Lifted from e2e/sortaballs-8-colors/solver.ts with additions for win detection.

export type Color = string & { readonly __brand?: "Color" }
export type GameState = Plate[]
export interface Level {
  initialState: GameState
  seed: number
  solutionLength: number
}
export interface LevelConfig {
  numMoves: number
  seed?: number
}
export interface Move {
  from: number
  to: number
}

// A move exactly as committed by the engine, including the moved group size.
// Persisted so a restored session can replay it and rebuild undo/reset history.
export interface CommittedMove extends Move {
  count: number
}
export type Plate = Color[]

export const DEFAULT_CAPACITY = 4

// For backward compatibility
export const CAPACITY = DEFAULT_CAPACITY

// Solved state: each of the first 3 plates has one color, plates 4-5 are empty.
export const SOLVED_STATE: GameState = [
  ["a", "a", "a", "a"],
  ["b", "b", "b", "b"],
  ["c", "c", "c", "c"],
  [],
  [],
]

export const isPlateFull = (
  plate: Plate,
  capacity: number = DEFAULT_CAPACITY,
): boolean=> plate.length >= capacity

// Per-plate capacity support: callers pass either a single number (uniform
// boards, all existing games) or a per-plate array (mixed-height boards). The
// uniform path is untouched and behaves identically.
export type Capacity = number | readonly number[]

export const capAt = (capacity: Capacity, index: number): number=>
  typeof capacity === "number"
    ? capacity
    : (capacity[index] ?? DEFAULT_CAPACITY)

export const plateTop = (plate: Plate): Color | undefined=>
  plate.length === 0 ? undefined : plate[plate.length - 1]

// Number of consecutive same-color balls on top of a plate (0 for empty).
// Used to decide how many balls a selection may grow to.
export const topRunLength = (plate: Plate): number=> {
  if (plate.length === 0) return 0
  const color = plateTop(plate)
  let run = 1
  for (let i = plate.length - 2; i >= 0; i--) {
    if (plate[i] !== color) break
    run++
  }
  return run
}

export const validMoves = (
  state: GameState,
  capacity: Capacity = DEFAULT_CAPACITY,
): Move[]=> {
  const moves: Move[] = []
  for (let src = 0; src < state.length; src++) {
    const srcPlate = state[src]
    if (srcPlate.length === 0) continue
    const topColor = plateTop(srcPlate)
    for (let dst = 0; dst < state.length; dst++) {
      if (dst === src) continue
      const dstPlate = state[dst]
      if (isPlateFull(dstPlate, capAt(capacity, dst))) continue
      if (dstPlate.length === 0) {
        moves.push({ from: src, to: dst })
        continue
      }
      if (plateTop(dstPlate) === topColor) {
        moves.push({ from: src, to: dst })
      }
    }
  }
  return moves
}

export const applyMove = (state: GameState, move: Move): GameState=> {
  const { from, to } = move
  const next = state.map(plate=> [...plate])
  const moving = next[from].pop()
  if (moving === undefined) {
    throw new Error(`applyMove: source plate ${from} is empty`)
  }
  next[to].push(moving)
  return next
}

// Move `count` balls from `from` to `to` in one shot, preserving order
// (bottom→top). Callers must validate with canMoveGroup first.
//
// ≪ OLD deep-copy implementation — superseded by the LIGHT variant below,
//   kept here (commented out) for reference. The old one re-copied every plate
//   on every move:
//
//   export const applyMoveGroup = (
//     state: GameState,
//     move: Move,
//     count: number,
//   ): GameState=> {
//     const { from, to } = move
//     const next = state.map(plate=> [...plate])
//     const source = next[from]
//     if (!source || count < 1) {
//       throw new Error(
//         `applyMoveGroup: cannot move ${count} ball(s) from plate ${from}`,
//       )
//     }
//     const moving = source.splice(source.length - count, count)
//     next[to].push(...moving)
//     return next
//   }
// ≫
//
// LIGHT variant: copies ONLY the two affected plates and aliases every other
// plate reference. Game state is immutable and plates are consumed read-only,
// so untouched stacks can be shared safely. Same values, but each move
// touches ~2 of 12 plates instead of all of them.
export const applyMoveGroup = (
  state: GameState,
  move: Move,
  count: number,
): GameState=> {
  const fromPlate = state[move.from]
  if (!fromPlate || count < 1) {
    throw new Error(
      `applyMoveGroup: cannot move ${count} ball(s) from plate ${move.from}`,
    )
  }
  const moving = fromPlate.slice(fromPlate.length - count)
  const next = state.slice()
  next[move.from] = fromPlate.slice(0, fromPlate.length - count)
  next[move.to] = (state[move.to] ?? []).concat(moving)
  return next
}

// Can `count` balls move from `from` to `to` under the group rules?
// - count is sane (≥ 1, ≤ source length) and from ≠ to
// - dest has room: not full and dest.length + count ≤ capacity
// - every moved ball shares the top color of the source
// - dest is empty or its top color matches
export const canMoveGroup = ({
  capacity = DEFAULT_CAPACITY,
  count,
  move,
  state,
}: {
  capacity?: Capacity
  count: number
  move: Move
  state: GameState
}): boolean=> {
  const { from, to } = move
  if (from === to) return false
  const srcPlate = state[from]
  const dstPlate = state[to]
  if (!srcPlate || !dstPlate || srcPlate.length === 0) return false
  if (count < 1 || count > srcPlate.length) return false
  const destCapacity = capAt(capacity, to)
  if (isPlateFull(dstPlate, destCapacity)) return false
  if (dstPlate.length + count > destCapacity) return false

  const color = plateTop(srcPlate)
  for (let i = 1; i < count; i++) {
    if (srcPlate[srcPlate.length - 1 - i] !== color) return false
  }
  if (dstPlate.length > 0 && plateTop(dstPlate) !== color) return false
  return true
}

const reverseOf = (move: Move): Move=> ({ from: move.to, to: move.from })

const sameMove = (a: Move, b: Move): boolean=>
  a.from === b.from && a.to === b.to
// Pick a random valid move (optionally avoiding reverse of last move).
// Useful for generating random puzzles from solved state.
export const pickRandomMove = (
  state: GameState,
  lastMove: Move | undefined,
  rng: ()=> number = Math.random,
): Move | undefined=> {
  const moves = validMoves(state)
  if (moves.length === 0) return undefined
  if (lastMove === undefined) {
    return moves[Math.floor(rng() * moves.length)]
  }
  const forbidden = reverseOf(lastMove)
  const nonReverse = moves.filter(m=> !sameMove(m, forbidden))
  if (nonReverse.length === 0) return moves[Math.floor(rng() * moves.length)]
  return nonReverse[Math.floor(rng() * nonReverse.length)]
}

export const pickMove = (
  state: GameState,
  lastMove: Move | undefined,
): Move | undefined=> {
  const moves = validMoves(state)
  if (moves.length === 0) return undefined
  if (lastMove === undefined) return moves[0]
  const forbidden = reverseOf(lastMove)
  const nonReverse = moves.find(m=> !sameMove(m, forbidden))
  return nonReverse ?? moves[0]
}

// A state is solved when every plate is either empty or contains only one color.
export const isSolved = (state: GameState): boolean=>
  state.every(plate=> plate.length === 0 || plate.every(c=> c === plate[0]))

// "Endspiel" (endgame): the board already has `settledPlates` or more plates
// holding a single color ("several plates of the same color"). Empty plates are
// not settled. This is a descriptive helper, NOT a gate for the near-win
// marker below — a board one move from solved can have just one settled plate,
// so gating on it would drop real near-wins. Use it for "almost done" hints.
export const isEndspiel = (state: GameState, settledPlates = 2): boolean=> {
  let settled = 0
  for (const plate of state) {
    if (plate.length === 0) continue
    if (!plate.every(c=> c === plate[0])) continue
    settled++
    if (settled >= settledPlates) return true
  }
  return false
}

// A board is one move from solved when a single legal move completes it. The
// game counts a whole same-color run as ONE move, so every group size from 1
// up to the source's top run is tried (only the full run can actually finish a
// color, but scanning all sizes keeps this rule-agnostic). An already-solved
// board is distance 0, not 1, so it returns false.
//
// To detect TWO moves from solved, grow this into a depth-2 search: for every
// group move whose result is not solved, run this same check on that result
// (or BFS bounded to depth 2), memoizing serialized boards so the branching
// factor stays bounded. That is roughly O(validMoves²) isSolved calls per
// check — fine for 10-12 plate boards, but consider a cheap pre-filter (e.g.
// isEndspiel()) so mid-game renders never pay for it.
export const isOneMoveFromSolved = (
  state: GameState,
  capacity: Capacity = DEFAULT_CAPACITY,
): boolean=> {
  if (isSolved(state)) return false
  // One group move can un-mix at most one plate (its source — the destination
  // is empty or already matches the moved color), so more than one mixed plate
  // can never be one move from solved. O(plates) bail instead of the O(V²)
  // scan below. run length is unchanged, so it stays rule-agnostic.
  if (mixedPlateCount(state) > 1) return false
  for (let from = 0; from < state.length; from++) {
    const source = state[from]
    if (!source || source.length === 0) continue
    const run = topRunLength(source)
    for (let count = 1; count <= run; count++) {
      for (let to = 0; to < state.length; to++) {
        const move: Move = { from, to }
        if (!canMoveGroup({ capacity, count, move, state })) continue
        if (isSolved(applyMoveGroup(state, move, count))) return true
      }
    }
  }
  return false
}

// True when the board can be completed in at most `maxMoves` legal group
// moves (0 → already solved, 1 → one move from solved, etc.). This is the
// depth-N generalization the isOneMoveFromSolved comment calls for: a bounded
// search over group moves, memoizing serialized boards per remaining-budget so
// duplicate states from different move orders are checked once. Roughly
// O(validMoves^N) isSolved calls per check — fine for 10-12 plate boards at
// small N; guard N low enough that mid-game renders never pay for it.
export const isSolvedWithinMoves = (
  state: GameState,
  capacity: Capacity = DEFAULT_CAPACITY,
  maxMoves: number,
): boolean=> {
  // Admissible pre-filter for GROUP moves: only non-empty plates holding more
  // than one color demand work, and a single group move repairs at most one
  // of them (the source — the destination is empty or already matches the
  // moved color). A per-ball buried count is NOT admissible here, because a
  // whole same-color run travels in one move (e.g. [y, x, x, x, x] plus an
  // empty plate is ONE group move from solved yet has 4 buried balls). The
  // mixed-plate bound prunes less but stays correct: if it exceeds the budget
  // the depth search can't win, so far-from-solved boards still bail out in
  // O(plates) instead of paying for the search.
  if (mixedPlateCount(state) > maxMoves) return false
  const memo = new Map<string, boolean>()
  const search = (board: GameState, movesLeft: number): boolean=> {
    if (isSolved(board)) return true
    if (movesLeft <= 0) return false
    const key = `${JSON.stringify(board)}|${movesLeft}`
    const cached = memo.get(key)
    if (cached !== undefined) return cached
    memo.set(key, false)
    for (let from = 0; from < board.length; from++) {
      const source = board[from]
      if (!source || source.length === 0) continue
      const run = topRunLength(source)
      for (let count = 1; count <= run; count++) {
        for (let to = 0; to < board.length; to++) {
          const move: Move = { from, to }
          if (!canMoveGroup({ capacity, count, move, state: board })) continue
          const next = applyMoveGroup(board, move, count)
          if (search(next, movesLeft - 1)) {
            memo.set(key, true)
            return true
          }
        }
      }
    }
    return false
  }
  return search(state, maxMoves)
}

// Number of non-empty plates holding more than one color — an admissible
// lower bound on the group moves still needed: each group move can un-mix at
// most one plate (its source). The per-ball buried count used by the
// unit-move solver (optimal-solver.ts) is deliberately not used here, since
// it overestimates when a whole run travels in a single move.
const mixedPlateCount = (state: GameState): number=> {
  let mixed = 0
  for (const plate of state) {
    if (plate.length === 0) continue
    if (!plate.every(c=> c === plate[0])) mixed++
  }
  return mixed
}

// Check if a specific move is legal under the game rules.
export const isValidMove = (
  state: GameState,
  move: Move,
  capacity: Capacity = DEFAULT_CAPACITY,
): boolean=> {
  const { from, to } = move
  if (from === to) return false
  const srcPlate = state[from]
  const dstPlate = state[to]
  if (srcPlate.length === 0) return false
  if (isPlateFull(dstPlate, capAt(capacity, to))) return false
  if (dstPlate.length === 0) return true
  return plateTop(dstPlate) === plateTop(srcPlate)
}

// Generate a random puzzle state by distributing balls randomly.
// This creates a mixed state that's not solved.
export const generateRandomPuzzle = (
  rng: ()=> number = Math.random,
): GameState=> {
  // Start with all balls
  const allBalls: Color[] = [
    "a",
    "a",
    "a",
    "a",
    "b",
    "b",
    "b",
    "b",
    "c",
    "c",
    "c",
    "c",
  ]

  // Shuffle the balls
  for (let i = allBalls.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    ;[allBalls[i], allBalls[j]] = [allBalls[j]!, allBalls[i]!]
  }

  // Distribute into plates (first 3 plates get 4 balls each, last 2 empty)
  const state: GameState = [[], [], [], [], []]
  for (let i = 0; i < 12; i++) {
    const plateIndex = Math.floor(i / 4)
    state[plateIndex]?.push(allBalls[i]!)
  }

  return state
}
